import { View, Views } from 'react-big-calendar';
import { isValidPhoneNumber } from 'react-phone-number-input';

import { blue, getFirstLetterInName } from 'enable-ui';

import { useAuthStore, userProfileSelector } from 'modules/auth/core';

import { CountryCode } from 'libphonenumber-js/types.cjs';

import { format, parseISO, setHours, setMinutes, setSeconds, subDays } from 'date-fns';
import { endsWith, includes, isNaN, isObject } from 'lodash';

import { LETTER_COLOR_CONFIG } from 'constants/avatar';
import { PATH } from 'constants/path';

import { trimObject } from './compile-data';

export const roundNumber = (num: number, decimal: number) => {
  return Number(num.toFixed(decimal));
};

export function hasEmptyValues<T>(obj: T): boolean {
  if (!isObject(obj)) {
    return true;
  }

  return Object.values(trimObject(obj)).some((el) => !el);
}

export const getUserColor = (fullName: string) => {
  if (!fullName) {
    return 'transparent';
  }

  const firstLetter = getFirstLetterInName(fullName, true);
  const keyColorConfig = Object.keys(LETTER_COLOR_CONFIG).find((key) =>
    key.includes(firstLetter.toUpperCase()),
  );

  if (keyColorConfig) {
    return LETTER_COLOR_CONFIG[
      keyColorConfig as 'AGMSY' | 'BHNTZ' | 'CIOU' | 'DJPV' | 'EKQW' | 'FLRX'
    ];
  }

  return blue['blue-5'];
};

export const checkRole = (roles: string[]) => {
  const userProfile = userProfileSelector(useAuthStore.getState());

  const roleKey = userProfile?.role?.key;

  if (includes(roles, roleKey)) {
    return true;
  }

  return false;
};

export const checkSuperAdmin = () => {
  const userProfile = userProfileSelector(useAuthStore.getState());

  const isSuperAdmin = userProfile?.isSuperAdmin;

  if (isSuperAdmin) {
    return true;
  }

  return false;
};

export const reorder = (list: any[], startIndex: number, endIndex: number) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);

  result.splice(endIndex, 0, removed);

  return result;
};

export const getTimeLine = (date: Date, time?: string, options?: { addEndTimeHour: number }) => {
  if (!time) {
    const currentDate = new Date(date);

    if (options?.addEndTimeHour) {
      return new Date(currentDate.setHours(currentDate.getHours() + options.addEndTimeHour));
    }

    return currentDate;
  }

  const [hourString, minuteString] = time.split(':');

  const hour = parseInt(hourString, 10);
  const minute = parseInt(minuteString, 10);

  const timeLine = setHours(setMinutes(date, minute), hour);

  return timeLine;
};

export const isTypeImage = (fileType = '') => {
  const typeImage: string[] = [
    'jpeg',
    'jpg',
    'bmp',
    'tif',
    'tiff',
    'ico',
    'raw',
    'psd',
    'gif',
    'png',
    'svg',
    'webp',
  ];
  const NAME_IMAGE = 'image/';
  let hasImage = false;

  typeImage.forEach((el) => {
    if (endsWith(fileType, el, NAME_IMAGE.length + el.length)) {
      hasImage = true;
    }
  });

  return hasImage;
};

export const convertToTimeString = (milliseconds: number, isFraction = false) => {
  const totalSeconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const fraction = Math.ceil((milliseconds % 1000) / 10);

  const minutesString = String(minutes).padStart(2, '0');
  const secondsString = String(seconds).padStart(2, '0');
  const fractionString = String(fraction);

  const timeString = `${minutesString} min ${secondsString}${isFraction ? `.${fractionString}` : ''} sec`;

  return timeString;
};

export const showMonth = (month: number) => {
  switch (month) {
    case 1:
      return 'January';

    case 2:
      return 'February';

    case 3:
      return 'March';

    case 4:
      return 'April';

    case 5:
      return 'May';

    case 6:
      return 'June';

    case 7:
      return 'July';

    case 8:
      return 'August';

    case 9:
      return 'September';

    case 10:
      return 'October';

    case 11:
      return 'November';

    case 12:
      return 'December';

    default:
      return '';
  }
};

export const getDateOfBirth = () => {
  const years: number[] = [-1];
  const months: string[] = [
    'Not select',
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const days: number[] = [];
  const currentYear = new Date().getFullYear();

  for (let i = currentYear - 100; i <= currentYear; i++) {
    years.push(i);
  }

  for (let i = 1; i <= 31; i++) {
    days.push(i);
  }

  return { years, months, days };
};

export const validateDateOfBirth = (day: number, month: number, year: number) => {
  if (
    !day ||
    !month ||
    !year ||
    typeof day !== 'number' ||
    typeof month !== 'number' ||
    typeof year !== 'number' ||
    isNaN(day) ||
    isNaN(month) ||
    isNaN(year)
  ) {
    return false;
  }

  // check for valid day range(1-31)
  if (!(1 <= day && day <= 31)) {
    return false;
  }

  // check for valid month range(1-12)
  if (!(1 <= month && month <= 12)) {
    return false;
  }

  // Check for days in each month, considering leap year
  const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  const daysInMonth: { [month: number]: number } = {
    1: 31,
    2: isLeapYear ? 29 : 28, // Adjust for leap year in February
    3: 31,
    4: 30,
    5: 31,
    6: 30,
    7: 31,
    8: 31,
    9: 30,
    10: 31,
    11: 30,
    12: 31,
  };

  // check if day is within the valid range for the given month
  if (day > daysInMonth[month]) {
    return false;
  }

  return true;
};

export const convertLevelStringToNumber = (levelString: string): number => {
  const match = levelString.match(/\d+/);

  return match ? parseInt(match[0], 10) : NaN;
};

export const isValidEmail = (email: string) => {
  if (!email) {
    return {
      isValid: true,
      message: '',
    };
  }

  if (email.length > 80) {
    return {
      isValid: false,
      message: 'Email is too long.',
    };
  }

  const emailRegex = /^[a-zA-Z0-9]+(?:[._][a-zA-Z0-9]+)*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  return emailRegex.test(email)
    ? {
        isValid: true,
        message: '',
      }
    : {
        isValid: false,
        message:
          'Format email is not valid: only letters (a-z), numbers (0-9), and periods (.) are allowed.',
      };
};

export const getWidthAndHeightVideo = (
  widthScreen: number,
  originWidth?: number,
  originHeight?: number,
) => {
  const maxHeight = widthScreen * 1.25;

  if (originWidth && originHeight) {
    return calculateDimensions(originWidth, originHeight, widthScreen, maxHeight);
  }

  return {
    width: undefined,
    height: undefined,
  };
};

export const calculateDimensions = (
  originWidth: number,
  originHeight: number,
  maxWidth: number,
  maxHeight: number,
) => {
  const videoOnScreenHeight = (maxWidth / originWidth) * originHeight;

  if (originWidth >= originHeight) {
    return {
      width: maxWidth,
      height: videoOnScreenHeight,
    };
  }

  if (videoOnScreenHeight <= maxHeight) {
    return {
      width: maxWidth,
      height: videoOnScreenHeight,
    };
  }

  return {
    width: maxWidth,
    height: maxHeight,
  };
};

export const validatePhoneNumber = (
  phone: string | undefined,
  countryCode: CountryCode,
): string | null => {
  if (phone) {
    const isCorrect = isValidPhoneNumber(phone, countryCode);

    if (!isCorrect) return 'Phone number is not valid.';
  }

  return null;
};

export const getPathDefault = (permission: string) => {
  switch (permission) {
    case 'Admins':
      return PATH.ADMINS;

    case 'Institutions':
      return PATH.INSTITUTIONS;

    case 'Library':
      return PATH.CONTENT;

    case 'Classes':
      return PATH.CLASSES;

    case 'Trainers':
      return PATH.TRAINERS;

    case 'Analytics':
      return PATH.ANALYTICS;

    case 'Institution info':
      return PATH.INSTITUTION_INFO;

    case 'Clients':
      return PATH.LEARNERS;

    case 'Calendar':
      return PATH.CALENDAR;

    default:
      return PATH.ERROR_404;
  }
};

export const getFormattedDate = (startDate: string) => {
  const date = parseISO(startDate);

  const day = format(date, 'do'); // 10th, 11th, 12th...
  const weekDay = format(date, 'EEEE'); // Monday, Tuesday...
  const month = format(date, 'MMMM'); // March, April...
  const year = format(date, 'yyyy'); // 2025

  return { day, date: weekDay, month, year };
};

export const getReminderTime = (eventTime: string): Date => {
  const eventDate = new Date(eventTime);

  // Subtract 1 day
  const reminderDate = subDays(eventDate, 1);

  // Set the time to 9:00 AM UTC
  const finalReminder = setHours(setMinutes(setSeconds(reminderDate, 0), 0), 9);

  return finalReminder;
};

export const getDateRange = (view: View, currentDate: Date) => {
  // For month view, get the first and last day of the month
  if (view === Views.MONTH) {
    const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const lastDay = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

    return {
      startDate: firstDay.toISOString(),
      endDate: lastDay.toISOString(),
    };
  }

  // For week view, get the entire week
  if (view === Views.WEEK) {
    const startOfWeek = new Date(currentDate);
    const day = startOfWeek.getDay();

    startOfWeek.setDate(startOfWeek.getDate() - day);

    const endOfWeek = new Date(startOfWeek);

    endOfWeek.setDate(endOfWeek.getDate() + 6);

    return {
      startDate: startOfWeek.toISOString(),
      endDate: endOfWeek.toISOString(),
    };
  }

  // For day view or default, return current day to next day
  return {
    startDate: new Date(currentDate.setHours(0, 0, 0, 0)).toISOString(),
    endDate: new Date(currentDate.getTime() + 24 * 60 * 60 * 1000).toISOString(),
  };
};

export const getNextRoundedHour = () => {
  const now = new Date();

  now.setMinutes(0, 0, 0);

  now.setHours(now.getHours() + 1);

  const startHours = String(now.getHours()).padStart(2, '0');

  const startTime = `${startHours}:00`;

  now.setHours(now.getHours() + 1);

  const endHours = String(now.getHours()).padStart(2, '0');

  const endTime = `${endHours}:00`;

  return { startTime, endTime };
};
